import { FC, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useMedia } from '../grid/hooks';

export type TConnatixOutstreamConfig = {
  embedScriptUrl: string;
  playerElementId: string;
};

const CONNATIX_OUTSTREAM_INJECTED_KEY = '__RIO_CONNATIX_OUTSTREAM_INJECTED';
const CONNATIX_WIDE_DESKTOP_BREAKPOINT_PX = 1750;
const CONNATIX_DEFAULT_WIDTH_PX = 300;
const CONNATIX_WIDE_DESKTOP_WIDTH_PX = 400;
const CONNATIX_OUTSTREAM_BASE_STYLE: CSSProperties = {
  position: 'fixed',
  right: 5,
  bottom: 5,
  aspectRatio: '16 / 9',
  zIndex: 1000,
  overflow: 'hidden',
  pointerEvents: 'none'
};

type TConnatixOutstreamProps = {
  config?: TConnatixOutstreamConfig;
};

function getPlacementScriptUrl(embedScriptUrl: string, playerElementId: string): string {
  const url = new URL(embedScriptUrl, window.location.href);
  url.searchParams.set('divId', playerElementId);
  return url.toString();
}

export const ConnatixOutstream: FC<TConnatixOutstreamProps> = ({ config }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isWideDesktop = useMedia([`(min-width: ${CONNATIX_WIDE_DESKTOP_BREAKPOINT_PX + 1}px)`], [true], false);
  const widthPx = isWideDesktop ? CONNATIX_WIDE_DESKTOP_WIDTH_PX : CONNATIX_DEFAULT_WIDTH_PX;
  const containerStyle = {
    ...CONNATIX_OUTSTREAM_BASE_STYLE,
    width: `min(${widthPx}px, calc(100vw - 10px))`
  };

  useEffect(() => {
    const win = window as any;
    if (win[CONNATIX_OUTSTREAM_INJECTED_KEY]) {
      return;
    }

    const container = containerRef.current;
    if (!config?.embedScriptUrl || !container) {
      console.error('[Connatix] missing placement embed configuration');
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = getPlacementScriptUrl(config.embedScriptUrl, config.playerElementId);
    script.onload = () => {
      container.style.pointerEvents = 'auto';
      win[CONNATIX_OUTSTREAM_INJECTED_KEY] = true;
      win.RIO_OnProviderLoaded?.();
    };
    script.onerror = () => {
      win.RIO_OnProviderBlocked?.();
    };

    container.appendChild(script);

    return () => {
      script.remove();
      delete win[CONNATIX_OUTSTREAM_INJECTED_KEY];
    };
  }, [config]);

  return <div id={config?.playerElementId} ref={containerRef} style={containerStyle} />;
};
